import {
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";

import { useMetaSdk } from "./MetaSdkProvider";

const META_MESSAGE_ORIGINS = [
  "https://www.facebook.com",
  "https://web.facebook.com"
];

const parseMetaMessage = event => {
  if (!META_MESSAGE_ORIGINS.includes(event.origin)) {
    return null;
  }

  let payload = event.data;

  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch (error) {
      return null;
    }
  }

  if (payload?.type !== "WA_EMBEDDED_SIGNUP") {
    return null;
  }

  return payload;
};

const useMetaEmbeddedSignup = () => {
  const {
    ready,
    loading: sdkLoading,
    error: sdkError,
    sdk
  } = useMetaSdk();

  const configId =
    process.env.REACT_APP_META_EMBEDDED_SIGNUP_CONFIG_ID || "";

  const [connecting, setConnecting] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [error, setError] = useState(null);

  const resolveRef = useRef(null);
  const rejectRef = useRef(null);
  const codeRef = useRef(null);
  const sessionRef = useRef(null);
  const timeoutRef = useRef(null);

  const clearTimeoutRef = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const resetPendingFlow = useCallback(() => {
    clearTimeoutRef();

    resolveRef.current = null;
    rejectRef.current = null;
    codeRef.current = null;
    sessionRef.current = null;
  }, [clearTimeoutRef]);

  const rejectFlow = useCallback(
    signupError => {
      setError(signupError);

      if (rejectRef.current) {
        rejectRef.current(signupError);
      }

      setConnecting(false);
      resetPendingFlow();
    },
    [resetPendingFlow]
  );

  const tryResolveFlow = useCallback(() => {
    const code = codeRef.current;
    const session = sessionRef.current;

    if (
      !code ||
      !session?.wabaId ||
      !session?.phoneNumberId
    ) {
      return;
    }

    if (resolveRef.current) {
      resolveRef.current({
        code,
        session
      });
    }

    setConnecting(false);
    resetPendingFlow();
  }, [resetPendingFlow]);

  useEffect(() => {
    const sessionInfoListener = event => {
      const payload = parseMetaMessage(event);

      if (!payload) {
        return;
      }

      if (payload.event === "FINISH") {
        const data = {
          wabaId: payload.data?.waba_id || null,
          phoneNumberId: payload.data?.phone_number_id || null,
          businessId: payload.data?.business_id || null,
          raw: payload
        };

        sessionRef.current = data;
        setSessionInfo(data);
        tryResolveFlow();
        return;
      }

      if (payload.event === "CANCEL") {
        const cancelError = new Error(
          "Conexão com a Meta cancelada."
        );

        cancelError.code = "META_SIGNUP_CANCELLED";
        rejectFlow(cancelError);
        return;
      }

      if (payload.event === "ERROR") {
        const signupError = new Error(
          payload.data?.error_message ||
            "A Meta não concluiu a conexão."
        );

        signupError.code =
          payload.data?.error_code || "META_SIGNUP_ERROR";

        rejectFlow(signupError);
      }
    };

    window.addEventListener("message", sessionInfoListener);

    return () => {
      window.removeEventListener(
        "message",
        sessionInfoListener
      );
    };
  }, [rejectFlow, tryResolveFlow]);

  useEffect(() => {
    return () => {
      resetPendingFlow();
    };
  }, [resetPendingFlow]);

  const connect = useCallback(() => {
    if (connecting) {
      return Promise.reject(
        new Error(
          "Uma conexão com a Meta já está em andamento."
        )
      );
    }

    if (!ready || !sdk) {
      return Promise.reject(
        sdkError ||
          new Error(
            "O SDK da Meta ainda não está disponível."
          )
      );
    }

    if (!configId) {
      return Promise.reject(
        new Error(
          "REACT_APP_META_EMBEDDED_SIGNUP_CONFIG_ID não configurado."
        )
      );
    }

    setConnecting(true);
    setSessionInfo(null);
    setError(null);

    codeRef.current = null;
    sessionRef.current = null;

    return new Promise((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;

      timeoutRef.current = setTimeout(() => {
        const timeoutError = new Error(
          "A Meta não concluiu a conexão dentro do tempo esperado."
        );

        timeoutError.code = "META_SIGNUP_TIMEOUT";
        rejectFlow(timeoutError);
      }, 180000);

      sdk.login(
        response => {
          const code = response?.authResponse?.code;

          if (code) {
            codeRef.current = code;
            tryResolveFlow();
            return;
          }

          const loginError = new Error(
            "Login com a Meta cancelado ou não autorizado."
          );

          loginError.code = "META_LOGIN_NOT_AUTHORIZED";
          rejectFlow(loginError);
        },
        {
          config_id: configId,
          response_type: "code",
          override_default_response_type: true,
          extras: {
            setup: {},
            featureType: "",
            sessionInfoVersion: "3"
          }
        }
      );
    });
  }, [
    configId,
    connecting,
    ready,
    sdk,
    sdkError,
    rejectFlow,
    tryResolveFlow
  ]);

  return {
    ready,
    sdkLoading,
    sdkError,
    connecting,
    sessionInfo,
    error,
    configId,
    connect
  };
};

export default useMetaEmbeddedSignup;
